import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { TokenData } from '../../types';
import { useSolanaTokens } from '../../hooks/useSolanaTokens';
import { formatMarketCap } from '../../utils/address';
import SolanaTradeModal from '../shared/modals/SolanaTradeModal';
import { SolanaWalletProvider } from '../SolanaWalletProvider';

const SOLANA_CHAIN_ID = 792703809; // Relay API Solana chain ID
const RELAY_API_BASE = 'https://api.relay.link';

// Solana Brand Colors
const SOL_PURPLE = '#9945FF';
const SOL_GREEN = '#14F195';
const SOL_DARK = '#333333';
const SOL_LIGHT = '#FFFFFF';

// Helper function to convert HTTP URLs to HTTPS
const ensureHttpsUrl = (url: string | null | undefined, fallback: string): string => {
  if (!url) return fallback;
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  if (!url.startsWith('http')) {
    return url;
  }
  return url;
};

// Helper function to format price with subscript notation for small values
const formatPrice = (price: number | null): React.ReactNode => {
  if (!price) return '--';
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  if (price >= 0.0001) return `$${price.toFixed(6)}`;
  
  // For very small prices, use subscript notation
  const priceStr = price.toFixed(20);
  const match = priceStr.match(/^0\.0*([1-9])/);
  if (match) {
    const zeros = priceStr.indexOf(match[1]) - 2;
    const significantDigits = priceStr.slice(priceStr.indexOf(match[1]), priceStr.indexOf(match[1]) + 2);
    return (
      <>
        $0.0<sub>{zeros}</sub>{significantDigits}
      </>
    );
  }
  return `$${price.toFixed(8)}`;
};

const TokenListContainer = styled.div`
  background: transparent;
  padding: 32px 24px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  color: #FFFFFF;
  
  @media (max-width: 700px) {
    padding: 16px 12px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${SOL_LIGHT};
  font-family: 'Inter', sans-serif;
  margin: 0;
  
  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  color: ${SOL_LIGHT};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    background: rgba(255, 255, 255, 0.15);
    border-color: ${SOL_PURPLE};
    box-shadow: 0 0 0 2px rgba(153, 69, 255, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TokenListTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
  width: 100%;
`;

const TokenListRow = styled.div<{ $hasPool?: boolean }>`
  display: grid;
  grid-template-columns: 40px 40px 1fr 140px 100px 80px;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  align-items: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: ${props => props.$hasPool ? 'pointer' : 'default'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: ${props => props.$hasPool ? 'translateY(-2px)' : 'none'};
    box-shadow: ${props => props.$hasPool ? '0 8px 16px rgba(153, 69, 255, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.2)'};
    border-color: ${props => props.$hasPool ? SOL_PURPLE : 'rgba(255, 255, 255, 0.1)'};
    background: ${props => props.$hasPool ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.05)'};
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 24px 28px 1.2fr 60px 50px 45px;
    gap: 3px;
    padding: 12px 8px;
    font-size: 10px;
  }
`;

const TokenListHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 40px 1fr 140px 100px 80px;
  gap: 12px;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
  
  & > div:first-child,
  & > div:nth-child(3) {
    text-align: left;
  }
  
  & > div:nth-child(4),
  & > div:nth-child(5) {
    text-align: right;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 24px 28px 1.2fr 60px 50px 45px;
    gap: 3px;
    padding: 10px 4px;
    font-size: 8px;
    letter-spacing: 0.2px;
  }
`;

const TokenRankCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const TokenListLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #FFFFFF;
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const TokenListInfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TokenListName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${SOL_LIGHT};
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const TokenListSymbol = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    font-size: 10px;
    gap: 4px;
  }
`;

const ContractAddressInline = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Monaco', 'Courier New', monospace;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: ${SOL_PURPLE};
  }
  
  @media (max-width: 768px) {
    font-size: 8px;
    gap: 2px;
  }
`;

const TokenListValueCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${SOL_LIGHT};
  font-family: 'Inter', sans-serif;
  text-align: right;
  
  @media (max-width: 768px) {
    font-size: 8px;
    white-space: nowrap;
    overflow: visible;
  }
`;

const QuickBuyButton = styled.button`
  background: ${SOL_LIGHT};
  border: 1px solid ${SOL_PURPLE};
  border-radius: 6px;
  color: ${SOL_PURPLE};
  font-size: 11px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px 12px;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background: ${SOL_PURPLE};
    color: #FFFFFF;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(153, 69, 255, 0.2);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
  flex-wrap: wrap;
  
  @media (max-width: 600px) {
    gap: 8px;
    margin-top: 24px;
  }
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.disabled ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.4)' : SOL_LIGHT};
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 8px 16px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    border-color: ${SOL_PURPLE};
    color: ${SOL_PURPLE};
    background: rgba(255, 255, 255, 0.15);
  }
`;

const PageNumberButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? SOL_PURPLE : 'transparent'};
  border: 1px solid ${props => props.$active ? SOL_PURPLE : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 6px;
  color: ${props => props.$active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 12px;
  font-weight: ${props => props.$active ? '600' : '400'};
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  padding: 6px 12px;
  transition: all 0.2s ease;
  min-width: 36px;
  
  &:hover {
    color: ${props => props.$active ? '#FFFFFF' : SOL_PURPLE};
    background: ${props => props.$active ? SOL_PURPLE : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${props => props.$active ? SOL_PURPLE : SOL_PURPLE};
  }
`;

const PaginationInfo = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', sans-serif;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #ef4444;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
`;

export const SolanaTokenList: React.FC<{ searchQuery: string; onSearchChange: (query: string) => void }> = ({ searchQuery, onSearchChange }) => {
  const navigate = useNavigate();
  const [selectedToken, setSelectedToken] = useState<{
    address: string;
    symbol: string;
    name: string;
    logo?: string | null;
    decimals?: number;
    poolAddress?: string;
    tokenVaultA?: string;
    tokenVaultB?: string;
    tickSpacing?: number;
  } | null>(null);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [tokenPrices, setTokenPrices] = useState<Map<string, number>>(new Map());
  const [pricesLoading, setPricesLoading] = useState(false);
  
  const {
    tokens,
    loading: tokensLoading,
    error: tokensError,
    currentPage,
    totalPages,
    goToPage,
    goToNextPage,
    goToPreviousPage
  } = useSolanaTokens(searchQuery);

  // Fetch prices for all tokens using Relay API
  useEffect(() => {
    if (tokens.length === 0) return;

    const fetchPrices = async () => {
      setPricesLoading(true);
      const pricesMap = new Map<string, number>();

      // Fetch prices with rate limiting
      for (const token of tokens) {
        const mintAddress = (token as any).mint_address || token.contract_address;
        if (!mintAddress) continue;

        try {
          const url = `${RELAY_API_BASE}/currencies/token/price?address=${mintAddress}&chainId=${SOLANA_CHAIN_ID}&referrer=mavrk.ink`;
          const response = await fetch(url);
          
          if (response.ok) {
            const data = await response.json();
            if (data.price && typeof data.price === 'number' && data.price > 0) {
              pricesMap.set(mintAddress.toLowerCase(), data.price);
            }
          }
          
          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.warn(`[SolanaTokenList] Failed to fetch price for ${token.symbol}:`, error);
        }
      }

      setTokenPrices(pricesMap);
      setPricesLoading(false);
    };

    fetchPrices();
  }, [tokens]);

  const handleTokenClick = (token: TokenData) => {
    const poolAddress = (token as any).pool_address;
    
    if (poolAddress) {
      // Open DexScreener for the token using the pool address
      window.open(`https://dexscreener.com/solana/${poolAddress}`, '_blank');
    } else {
      // If no pool, open Solscan using the mint address
      const mintAddress = (token as any).mint_address || token.contract_address;
      window.open(`https://solscan.io/token/${mintAddress}`, '_blank');
    }
  };

  const handleTradeClick = (token: TokenData, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the row click
    const mintAddress = (token as any).mint_address || token.contract_address;
    const poolAddress = (token as any).pool_address;
    
    if (poolAddress) {
      setSelectedToken({
        address: mintAddress,
        symbol: token.symbol,
        name: token.name,
        logo: token.logo_url,
        decimals: (token as any).decimals || 9,
        poolAddress: poolAddress,
        tokenVaultA: (token as any).token_vault_a,
        tokenVaultB: (token as any).token_vault_b,
        tickSpacing: (token as any).tick_spacing || 128,
      });
      setIsTradeModalOpen(true);
    }
  };

  if (tokensLoading) {
    return (
      <TokenListContainer>
        <LoadingMessage>Loading Solana tokens...</LoadingMessage>
      </TokenListContainer>
    );
  }

  if (tokensError) {
    return (
      <TokenListContainer>
        <ErrorMessage>Error: {tokensError}</ErrorMessage>
      </TokenListContainer>
    );
  }

  if (tokens.length === 0) {
    return (
      <TokenListContainer>
        <EmptyMessage>
          {searchQuery ? 'No tokens found matching your search.' : 'No tokens deployed on Solana yet.'}
        </EmptyMessage>
      </TokenListContainer>
    );
  }

  return (
    <TokenListContainer>
      <SectionHeader>
        <SectionTitle>Tokens</SectionTitle>
      </SectionHeader>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search by name, ticker, address..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </SearchContainer>

      <TokenListTable>
        <TokenListHeader>
          <div>#</div>
          <div></div>
          <div>TOKEN</div>
          <div style={{ textAlign: 'right' }}>MARKET CAP</div>
          <div style={{ textAlign: 'right' }}>PRICE</div>
          <div style={{ textAlign: 'center' }}>TRADE</div>
        </TokenListHeader>
        {tokens.map((token, index) => {
          const mintAddress = (token as any).mint_address || token.contract_address;
          const poolAddress = (token as any).pool_address;
          const priceUSD = tokenPrices.get(mintAddress?.toLowerCase() || '');
          const marketCap = priceUSD ? formatMarketCap(priceUSD) : '--';

          return (
            <TokenListRow 
              key={index} 
              $hasPool={!!poolAddress}
              onClick={() => handleTokenClick(token)}
            >
              <TokenRankCell>{index + 1 + (currentPage - 1) * 20}</TokenRankCell>
              <TokenListLogo
                src={ensureHttpsUrl(token.logo_url, 'https://placehold.co/32x32/9945FF/FFFFFF?text=SOL')}
                alt={`${token.name} Logo`}
              />
              <TokenListInfoCell>
                <TokenListName>{token.name}</TokenListName>
                <TokenListSymbol>
                  ${token.symbol}
                  <ContractAddressInline
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(mintAddress);
                      const target = e.currentTarget.querySelector('span');
                      if (target) {
                        const originalText = target.textContent;
                        target.textContent = 'Copied!';
                        setTimeout(() => {
                          if (target) {
                            target.textContent = originalText;
                          }
                        }, 1000);
                      }
                    }}
                    title="Click to copy mint address"
                  >
                    <span>
                      {typeof window !== 'undefined' && window.innerWidth <= 768
                        ? `${mintAddress.slice(0, 4)}...${mintAddress.slice(-4)}`
                        : `${mintAddress.slice(0, 6)}...${mintAddress.slice(-4)}`
                      }
                    </span>
                    <span>📋</span>
                  </ContractAddressInline>
                </TokenListSymbol>
              </TokenListInfoCell>
              <TokenListValueCell>{marketCap}</TokenListValueCell>
              <TokenListValueCell>{formatPrice(priceUSD ?? null)}</TokenListValueCell>
              <TokenListValueCell>
                {poolAddress ? (
                  <QuickBuyButton
                    onClick={(e) => handleTradeClick(token, e)}
                    title="Trade on Orca"
                    style={{ cursor: 'pointer' }}
                  >
                    Trade
                  </QuickBuyButton>
                ) : (
                  <span style={{ color: '#999', fontSize: '11px' }}>No Pool</span>
                )}
              </TokenListValueCell>
            </TokenListRow>
          );
        })}
      </TokenListTable>

      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationButton
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            title="Previous Page"
          >
            ←
          </PaginationButton>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {(() => {
              const pages = [];
              const maxVisiblePages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              if (startPage > 1) {
                pages.push(
                  <PageNumberButton key={1} onClick={() => goToPage(1)} $active={currentPage === 1}>
                    1
                  </PageNumberButton>
                );
                if (startPage > 2) {
                  pages.push(<span key="ellipsis1" style={{ color: 'rgba(255, 255, 255, 0.5)', padding: '0 5px' }}>...</span>);
                }
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <PageNumberButton
                    key={i}
                    onClick={() => goToPage(i)}
                    $active={currentPage === i}
                  >
                    {i}
                  </PageNumberButton>
                );
              }

              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(<span key="ellipsis2" style={{ color: 'rgba(255, 255, 255, 0.5)', padding: '0 5px' }}>...</span>);
                }
                pages.push(
                  <PageNumberButton
                    key={totalPages}
                    onClick={() => goToPage(totalPages)}
                    $active={currentPage === totalPages}
                  >
                    {totalPages}
                  </PageNumberButton>
                );
              }

              return pages;
            })()}
          </div>

          <PaginationButton
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            title="Next Page"
          >
            →
          </PaginationButton>

          <PaginationInfo>
            Page {currentPage} of {totalPages}
          </PaginationInfo>
        </PaginationContainer>
      )}

      <SolanaWalletProvider>
        <SolanaTradeModal
          isOpen={isTradeModalOpen}
          onClose={() => {
            setIsTradeModalOpen(false);
            setSelectedToken(null);
          }}
          token={selectedToken}
        />
      </SolanaWalletProvider>
    </TokenListContainer>
  );
};

export default SolanaTokenList;

